import styled from "styled-components";
import useRegisterNotice from "features/owner/hooks/useRegisterNotice";
import { FiImage, FiUser } from "react-icons/fi";
import { Button, TextButton } from "shared/components/Button";
import Modal from "shared/components/Modal";
import { parseDate } from "shared/utils/dateUtils";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface ViewNoticeProps {
    clickedNotice: {
        id: string;
        writer: string;
    };
    isBranch: boolean;
}

const ViewNotice = ({ clickedNotice, isBranch }: ViewNoticeProps) => {
    const {
        inputs,
        onChange,
        inputImageRef,
        onImageInput,
        onImageInputClick,
        showImageListView,
        onSaveNoticeClick,
        isSucceed,
        setIsSucceed,
        onDeleteClickHandler,
        isOpenDeleteSucceedModal,
        setIsOpenDeleteSucceedModal,
    } = useRegisterNotice(clickedNotice, isBranch, true);

    return (
        <Container>
            <Modal isOpen={isSucceed} title="SUCCESS" content="저장되었습니다" onClose={() => setIsSucceed(false)} />
            <Modal
                isOpen={isOpenDeleteSucceedModal}
                title="SUCCESS"
                content="삭제되었습니다 새로고침을 눌러주세요"
                onClose={() => setIsOpenDeleteSucceedModal(false)}
            />
            <Header>
                <TitleInput
                    placeholder="제목을 입력해 주세요"
                    name="title"
                    value={inputs.title}
                    onChange={onChange}
                />
                <IconButton type="button" onClick={onImageInputClick} aria-label="이미지 추가">
                    <FiImage size={18} />
                </IconButton>
                <input
                    type="file"
                    ref={inputImageRef}
                    accept="image/*"
                    onChange={onImageInput}
                    style={{ display: "none" }}
                />
            </Header>
            <Meta>
                {clickedNotice.writer ? (
                    <Chip>
                        <FiUser size={14} />
                        {clickedNotice.writer === "master" ? "본사" : "학원"}
                    </Chip>
                ) : null}
                <Chip>
                    <Label htmlFor="notice-expires">만료일</Label>
                    <DateInput
                        id="notice-expires"
                        type="date"
                        name="expires_at"
                        value={inputs.expires_at || ""}
                        onChange={onChange}
                        min={parseDate(new Date())}
                    />
                </Chip>
            </Meta>
            <ContentArea
                name="content"
                value={inputs.content}
                onChange={onChange}
                placeholder="내용을 입력해 주세요"
            />
            <Images>{showImageListView()}</Images>
            <Footer>
                {!isBranch ? (
                    <Targets>
                        <CheckLabel>
                            <Checkbox type="checkbox" name="student" checked={inputs.student} onChange={onChange} />
                            학생용 공지
                        </CheckLabel>
                        <CheckLabel>
                            <Checkbox type="checkbox" name="teacher" checked={inputs.teacher} onChange={onChange} />
                            교사용 공지
                        </CheckLabel>
                    </Targets>
                ) : <span />}
                <Actions>
                    <TextButton name="삭제하기" onButtonClick={onDeleteClickHandler} />
                    <Button name="저장하기" onButtonClick={onSaveNoticeClick} />
                </Actions>
            </Footer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 22px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const TitleInput = styled.input`
    flex: 1;
    min-width: 0;
    font-size: 18px;
    font-weight: 700;
    border: none;
    outline: none;
    padding: 4px 0;
    color: ${hw.text};
    background: transparent;
    letter-spacing: -0.02em;

    &::placeholder {
        color: ${hw.textSubtle};
        font-weight: 600;
    }
`;

const IconButton = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    width: 36px;
    height: 36px;
    border-radius: 8px;
    cursor: pointer;
    color: ${hw.textMuted};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
        background: ${hw.brandSoft};
        border-color: ${hw.brandMuted};
        color: ${hw.brand};
    }
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const Chip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 8px;
    color: ${hw.textMuted};
    font-size: 13px;
    font-weight: 600;
`;

const Label = styled.label`
    font-size: 12px;
    font-weight: 600;
    color: ${hw.textMuted};
`;

const DateInput = styled.input`
    border: none;
    outline: none;
    background: transparent;
    color: ${hw.text};
    font-size: 13px;
    font-weight: 600;
`;

const ContentArea = styled.textarea`
    flex: 1;
    min-height: 180px;
    border: 1px solid ${hw.border};
    border-radius: 12px;
    outline: none;
    resize: none;
    font-size: 14px;
    line-height: 1.6;
    padding: 12px 14px;
    color: ${hw.text};
    background: ${hw.bg};
    ${scrollbar}

    &:focus {
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
        background: ${hw.surface};
    }

    &::placeholder {
        color: ${hw.textSubtle};
    }
`;

const Images = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
    max-height: 180px;
    overflow: auto;
    ${scrollbar}

    img {
        width: 100%;
        height: 100px;
        object-fit: cover;
        border-radius: 10px;
        border: 1px solid ${hw.border};
        background: ${hw.bg};
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Targets = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
`;

const CheckLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: ${hw.text};
    font-size: 13px;
    font-weight: 600;
`;

const Checkbox = styled.input`
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: ${hw.brand};
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
    margin-left: auto;
`;

export default ViewNotice;
